import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toeicApi } from "../services/toeicApi";
import { queryKeys } from "./queryKeys";

export const useAnswersQuery = (keySearch = "") =>
  useQuery({
    queryKey: queryKeys.answers.list(keySearch),
    queryFn: () => toeicApi.getAnswers(keySearch),
  });

export const useAnswerQuery = (id, options = {}) =>
  useQuery({
    queryKey: queryKeys.answers.detail(id),
    queryFn: () => toeicApi.getAnswer(id),
    enabled: Boolean(id) && (options.enabled ?? true),
  });

export const useDeleteAnswerMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: toeicApi.deleteAnswer,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.answers.all });
    },
  });
};

export const useDocumentsQuery = (keySearch = "") =>
  useQuery({
    queryKey: queryKeys.documents.list(keySearch),
    queryFn: () => toeicApi.getDocuments(keySearch),
  });

export const useDocumentQuery = (id, options = {}) =>
  useQuery({
    queryKey: queryKeys.documents.detail(id),
    queryFn: () => toeicApi.getDocument(id),
    enabled: Boolean(id) && (options.enabled ?? true),
  });

export const useDeleteDocumentMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: toeicApi.deleteDocument,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.documents.all });
    },
  });
};

export const useUsersQuery = (keySearch = "") =>
  useQuery({
    queryKey: queryKeys.users.list(keySearch),
    queryFn: () => toeicApi.getUsers(keySearch),
    select: (data) => data?.metadata || [],
  });

export const useBanUserMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: toeicApi.banUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.users.all });
    },
  });
};

export const useActiveSlidersQuery = () =>
  useQuery({
    queryKey: queryKeys.sliders.active(),
    queryFn: toeicApi.getActiveSliders,
  });

export const useSliderQuery = (id, options = {}) =>
  useQuery({
    queryKey: queryKeys.sliders.detail(id),
    queryFn: () => toeicApi.getSlider(id),
    enabled: Boolean(id) && (options.enabled ?? true),
  });

export const useSliderCollectionsQuery = (keySearch = "") =>
  useQuery({
    queryKey: queryKeys.sliders.collections(keySearch),
    queryFn: () => toeicApi.getCollections(keySearch),
  });

export const useSlidersByCollectionQuery = (collection) =>
  useQuery({
    queryKey: queryKeys.sliders.byCollection(collection),
    queryFn: () => toeicApi.getSlidersByCollection(collection),
    enabled: Boolean(collection),
  });

export const useToggleSliderMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: toeicApi.toggleSlider,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.sliders.all });
    },
  });
};

export const useDeleteSliderMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: toeicApi.deleteSlider,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.sliders.all });
    },
  });
};

export const useDeleteCollectionMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: toeicApi.deleteCollection,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.sliders.all });
    },
  });
};

export const useHistoryRowsQuery = (userID) =>
  useQuery({
    queryKey: queryKeys.history.byUser(userID),
    queryFn: async () => {
      const histories = await toeicApi.getHistoryOfUser(userID);
      const rows = await Promise.all(
        histories.map(async (history) => {
          const answer = await toeicApi.getAnswer(history.history_answer_id);

          return {
            historyID: history._id,
            answerID: history.history_answer_id,
            name: answer?.name || "Unknown test",
            score: history.history_score || 0,
            createdAt: history.createdAt,
            day: new Date(history.createdAt).toLocaleString(),
          };
        })
      );

      return rows.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    },
    enabled: Boolean(userID),
  });

export const useProfileQuery = (userID) =>
  useQuery({
    queryKey: queryKeys.history.profile(userID),
    queryFn: async () => {
      const [histories, highestScore, foundUser] = await Promise.all([
        toeicApi.getHistoryOfUser(userID),
        toeicApi.getHighestOfUser(userID),
        toeicApi.getUser(userID),
      ]);
      const infoUser = foundUser?.metadata?.user;
      const birthDate = new Date(infoUser?.birthday || "2000-01-01");

      return {
        name: infoUser?.name || "",
        email: infoUser?.email || "",
        avatar: infoUser?.avatar || "",
        phone: infoUser?.phone || "",
        gender: infoUser?.gender || "male",
        birthDay: birthDate.getDate().toString().padStart(2, "0"),
        birthMonth: (birthDate.getMonth() + 1).toString().padStart(2, "0"),
        birthYear: birthDate.getFullYear().toString(),
        createdAt: new Date(
          infoUser?.createdAt || "2024-01-01"
        ).toLocaleDateString(),
        totalTests: histories.length,
        highestScore: highestScore?.history_highest_score || 0,
      };
    },
    enabled: Boolean(userID),
  });

export const useRankingDetailQuery = (answerID) =>
  useQuery({
    queryKey: queryKeys.history.rankingByAnswer(answerID),
    queryFn: async () => {
      const [answerInfo, rankingList] = await Promise.all([
        toeicApi.getAnswer(answerID),
        toeicApi.getRankingListOfTest(answerID),
      ]);

      const rankings = await Promise.all(
        rankingList.map(async (ranking) => {
          const userInfo = await toeicApi.getUser(ranking.userID);
          return {
            ...ranking,
            userName: userInfo?.metadata?.user?.name || "Unknown user",
          };
        })
      );

      return {
        answerInfo,
        rankings: rankings.sort(
          (a, b) =>
            (b.highestScoreRecord?.history_score || 0) -
            (a.highestScoreRecord?.history_score || 0)
        ),
      };
    },
    enabled: Boolean(answerID),
  });

export const useCommentsQuery = (answerID) =>
  useQuery({
    queryKey: queryKeys.comments.byAnswer(answerID),
    queryFn: () => toeicApi.getComments(answerID),
    enabled: Boolean(answerID),
  });

export const useDeleteCommentMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: toeicApi.deleteComment,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.comments.byAnswer(variables.data.answer_id),
      });
    },
  });
};

export const usePublicFlashcardDecksQuery = (keySearch = "") =>
  useQuery({
    queryKey: queryKeys.flashcards.publicDecks(keySearch),
    queryFn: () => toeicApi.getPublicFlashcardDecks(keySearch),
  });

export const useMyFlashcardDecksQuery = ({
  accessToken,
  userID,
  axiosJWT,
  keySearch = "",
}) =>
  useQuery({
    queryKey: queryKeys.flashcards.myDecks(userID, keySearch),
    queryFn: () =>
      toeicApi.getMyFlashcardDecks({ accessToken, userID, axiosJWT, keySearch }),
    enabled: Boolean(accessToken && userID && axiosJWT),
  });

export const useFlashcardCardsQuery = ({
  deckID,
  accessToken,
  userID,
  axiosJWT,
  isMine = false,
}) =>
  useQuery({
    queryKey: queryKeys.flashcards.cards(deckID, isMine ? userID : "public"),
    queryFn: () =>
      toeicApi.getFlashcardCards({
        deckID,
        accessToken,
        userID,
        axiosJWT,
        isMine,
      }),
    enabled: Boolean(deckID) && (!isMine || Boolean(accessToken && userID && axiosJWT)),
  });

export const useFlashcardWordSuggestionsQuery = (keySearch = "") =>
  useQuery({
    queryKey: queryKeys.flashcards.wordSuggestions(keySearch),
    queryFn: () => toeicApi.getFlashcardWordSuggestions(keySearch),
    enabled: keySearch.trim().length >= 2,
    staleTime: 1000 * 60 * 10,
  });

export const useLookupFlashcardWordMutation = () =>
  useMutation({
    mutationFn: toeicApi.lookupFlashcardWord,
  });

export const useCreateFlashcardDeckMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: toeicApi.createFlashcardDeck,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.flashcards.all });
    },
  });
};

export const useUpdateFlashcardDeckMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: toeicApi.updateFlashcardDeck,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.flashcards.all });
    },
  });
};

export const useDeleteFlashcardDeckMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: toeicApi.deleteFlashcardDeck,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.flashcards.all });
    },
  });
};

export const useCreateFlashcardCardMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: toeicApi.createFlashcardCard,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.flashcards.all });
    },
  });
};

export const useDeleteFlashcardCardMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: toeicApi.deleteFlashcardCard,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.flashcards.all });
    },
  });
};

export const useReviewFlashcardCardMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: toeicApi.reviewFlashcardCard,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.flashcards.all });
    },
  });
};
